import { Box, Typography, useMediaQuery } from "@mui/material";
import React, { useState, useEffect } from "react";
import Header from "../../Components/Common/Header";
import HomePageSearchBar from "../../Components/Common/HomePageSearchBar";
import ProductCards from "../../Components/Home/ProductCards";
import Footer from "../../Components/Common/Footer";
import NewArrivalShowcase from "../../Components/Home/NewArrivalShowcase";
import OnSaleShowcase from "../../Components/Home/OnSaleShowcase";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  collectionsQuery,
  fetchCollections,
} from "../../redux/slices/collectionsSlice";
import { fetchHomepageBrandsShowcase } from "../../redux/slices/brandsShowcaseSlice";
import DynamicShowcase from "../../Components/Home/DynamicShowcase";
import ImageSearchPromo from "../../Components/Home/ImageSearchPromo";

const HomePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { collections } = useSelector((state) => state.collections);
  const { newArrivals, onSale } = useSelector((state) => state.brandsShowcase);

  useEffect(() => {
    if (!collections || collections.length === 0) {
      dispatch(fetchCollections());
    }
    if (!newArrivals || Object.keys(newArrivals).length === 0) {
      dispatch(fetchHomepageBrandsShowcase());
    }
  }, [dispatch, collections.length, newArrivals]);

  const smallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        background: "#FFFFFF",
        minHeight: "100vh",
        width: "100%",
        overflowX: "hidden",
      }}
    >
      <Header />

      <HomePageSearchBar />

      <Box
        sx={{
          background: "linear-gradient(180deg, #FAFAFA 0%, #FFFFFF 100%)",
          borderTop: "1px solid #F0F0F0",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: "60%",
            height: "1px",
            background: "linear-gradient(90deg, transparent, rgba(255,107,0,0.2), transparent)"
          }
        }}
      >
        <DynamicShowcase />
      </Box>

      <Box
        sx={{
          maxWidth: "1200px",
          mx: "auto",
          px: smallScreen ? 2 : 4,
          pt: smallScreen ? 2 : 3, // Condensed space above Brands header
          pb: 0, // Removed bottom padding since collections section has top padding
        }}
      >
        <Box sx={{ mb: 2 }}>

          <OnSaleShowcase onSaleData={onSale} />
          <NewArrivalShowcase newArrivalsData={newArrivals} />
        </Box>
      </Box>

      <ImageSearchPromo />

      <Box
        sx={{
          maxWidth: "1200px",
          mx: "auto",
          px: smallScreen ? 2 : 4,
          pt: { xs: 4, md: 6 }, // Increased top spacing slightly since ImageSearchPromo pb is 0
          pb: smallScreen ? 3 : 4, // Condensed space before Footer
        }}
      >

        {collections &&
          collections?.map((collection, index) => {
            const collectionHeading = collectionsQuery.find(
              (item) => item.query === collection?.query,
            )?.heading;

            // Break down the heading to make the last word italicized for editorial look
            const words = collectionHeading ? collectionHeading.split(" ") : [];
            const lastWord = words.pop();
            const restOfHeading = words.join(" ");

            return (
              <Box
                key={index}
                sx={{
                  mb: smallScreen ? 4 : 5,
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Lora', serif",
                    fontSize: { xs: "18px", md: "28px" },
                    fontWeight: 500,
                    color: "#111",
                    mb: { xs: 1, md: 2 },
                    textTransform: "capitalize",
                    letterSpacing: "-0.02em",
                    textAlign: "left",
                  }}
                >
                  {restOfHeading}{" "}
                  <Box component="span" sx={{ fontStyle: "italic", color: "#FF6B00" }}>
                    {lastWord}
                  </Box>
                </Typography>

                {/* Highlighted AI Query Prompt */}
                <Box
                  onClick={() => navigate("/search?q=" + encodeURIComponent(collection?.query))}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    cursor: "pointer",
                    backgroundColor: "rgba(125, 211, 224, 0.08)",
                    border: "1px solid rgba(125, 211, 224, 0.25)",
                    borderRadius: "100px",
                    px: { xs: 1.5, md: 2.5 },
                    py: { xs: 0.5, md: 0.75 },
                    mb: { xs: 2.5, md: 3 },
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: "rgba(125, 211, 224, 0.15)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 12px rgba(125, 211, 224, 0.15)"
                    }
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5abccb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '10px', opacity: 0.8 }}>
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                  <Typography
                    sx={{
                      fontFamily: "Poppins-Medium",
                      fontSize: "13px",
                      color: "#5abccb",
                      letterSpacing: "0.5px"
                    }}
                  >
                    "{collection?.query}"
                  </Typography>
                </Box>

                <ProductCards productData={collection.products || []} variant={index % 3} />
              </Box>
            );
          })}
      </Box>

      <Footer />
    </Box>
  );
};

export default HomePage;
